/**
 * GuideContentItem Component
 *
 * Renders a single content item from a guide with support for:
 * - Title, subtitle, description
 * - Images (responsive)
 * - Videos (with controls)
 * - External article links
 */

import React from 'react';
import { Box, Typography, Link, useTheme } from '@mui/material';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';

export interface GuideContentItemProps {
  /** Title of the content item */
  title?: string;

  /** Subtitle of the content item */
  subtitle?: string;

  /** Description text */
  description?: string;

  /** Image URL */
  image?: string;

  /** Video URL */
  video?: string;

  /** External article link */
  fullArticleLink?: string;
}

export const GuideContentItem: React.FC<GuideContentItemProps> = ({
  title,
  subtitle,
  description,
  image,
  video,
  fullArticleLink,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
      }}
    >
      {/* Title */}
      {title && (
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: theme.typography.fontWeightMedium,
          }}
        >
          {title}
        </Typography>
      )}

      {/* Subtitle */}
      {subtitle && (
        <Typography
          variant="subtitle1"
          sx={{
            color: theme.palette.text.secondary,
            fontStyle: 'italic',
          }}
        >
          {subtitle}
        </Typography>
      )}

      {/* Image */}
      {image && (
        <Box
          component="img"
          src={image}
          alt={title || 'Guide image'}
          sx={{
            width: '100%',
            maxHeight: 400,
            objectFit: 'contain',
            borderRadius: theme.shape.borderRadius,
            border: `1px solid ${theme.palette.divider}`,
          }}
        />
      )}

      {/* Video */}
      {video && (
        <Box
          component="video"
          controls
          src={video}
          sx={{
            width: '100%',
            maxHeight: 400,
            borderRadius: theme.shape.borderRadius,
            border: `1px solid ${theme.palette.divider}`,
          }}
        />
      )}

      {/* Description */}
      {description && (
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.primary,
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
          }}
        >
          {description}
        </Typography>
      )}

      {/* Full Article Link */}
      {fullArticleLink && (
        <Link
          href={fullArticleLink}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: theme.spacing(0.5),
            color: theme.palette.primary.main,
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          Read full article
          <OpenInNewIcon fontSize="small" />
        </Link>
      )}
    </Box>
  );
};

GuideContentItem.displayName = 'GuideContentItem';
